import React from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area,
  ScatterChart, Scatter, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell, ResponsiveContainer
} from 'recharts';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

const ChartRenderer = ({ chartData }) => {
  if (!chartData || !chartData.type) {
    return null;
  }

  const { type, data, config = {} } = chartData;
  const { xKey, yKey, title, colors = COLORS } = config;

  const renderChart = () => {
    switch (type.toLowerCase()) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xKey || 'name'} />
              <YAxis />
              <Tooltip />
              <Legend />
              {Object.keys(data[0] || {})
                .filter(key => key !== (xKey || 'name'))
                .map((key, index) => (
                  <Bar key={key} dataKey={key} fill={colors[index % colors.length]} />
                ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xKey || 'name'} />
              <YAxis />
              <Tooltip />
              <Legend />
              {Object.keys(data[0] || {})
                .filter(key => key !== (xKey || 'name'))
                .map((key, index) => (
                  <Line 
                    key={key} 
                    type="monotone" 
                    dataKey={key} 
                    stroke={colors[index % colors.length]}
                    strokeWidth={2}
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xKey || 'name'} />
              <YAxis />
              <Tooltip />
              <Legend />
              {Object.keys(data[0] || {})
                .filter(key => key !== (xKey || 'name'))
                .map((key, index) => (
                  <Area 
                    key={key} 
                    type="monotone" 
                    dataKey={key} 
                    stackId="1"
                    stroke={colors[index % colors.length]}
                    fill={colors[index % colors.length]}
                  />
                ))}
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                dataKey={yKey || 'value'}
                nameKey={xKey || 'name'}
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'scatter':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xKey || 'x'} />
              <YAxis dataKey={yKey || 'y'} />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} />
              <Legend />
              <Scatter name="Data Points" data={data} fill={colors[0]} />
            </ScatterChart>
          </ResponsiveContainer>
        );

      case 'radar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={data}>
              <PolarGrid />
              <PolarAngleAxis dataKey={xKey || 'subject'} />
              <PolarRadiusAxis />
              <Radar 
                name="Values" 
                dataKey={yKey || 'value'} 
                stroke={colors[0]} 
                fill={colors[0]} 
                fillOpacity={0.6} 
              />
              <Legend />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <div className="text-sm text-gray-500">
            Unsupported chart type: {type}
          </div>
        );
    }
  };

  return (
    <div className="my-6 p-4 bg-white border rounded-lg shadow-sm">
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      )}
      {renderChart()}
    </div>
  );
};

// Helper function to parse chart JSON from text
export const extractChartData = (text) => {
  const charts = [];
  
  // Look for JSON chart data blocks in format: ```chart-json ... ```
  const chartRegex = /```chart-json\s*([\s\S]*?)```/g;
  let match;
  
  while ((match = chartRegex.exec(text)) !== null) {
    try {
      const chartData = JSON.parse(match[1].trim());
      charts.push(chartData);
    } catch (e) {
      console.error('Failed to parse chart JSON:', e);
    }
  }
  
  return charts;
};

// Component to render text with embedded charts
export const TextWithCharts = ({ text }) => {
  const charts = extractChartData(text);
  
  if (charts.length === 0) {
    return null;
  }
  
  return (
    <div className="space-y-4">
      {charts.map((chart, index) => (
        <ChartRenderer key={index} chartData={chart} />
      ))}
    </div>
  );
};

export default ChartRenderer;
